// Concrete calendar-date resolution for the holidays defined in seed/holidays.js.
//
// The Holiday Schedule app only ever asks "for holiday H with rule R in year Y,
// which parent has it?" — it never asks "which real dates does it fall on?".
// The Parenting Time app + calendar views + .ics feed need actual dates.
//
// Everything here is a pure function. All Date objects returned are in local time,
// day-precision (hours/minutes are 00:00:00 unless the caller sets them).

import { SEED_HOLIDAYS, PAIRED_DAY_RANGES } from './seed/holidays.js'

// --- Day helpers ---------------------------------------------------------

// Returns a Date at local midnight for (year, month, day). Month is 0-indexed.
function dateAt(year, month, day) {
  return new Date(year, month, day)
}

// Adds N days (can be negative) and returns a new Date.
function addDays(date, n) {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}

// Returns the Nth `dow` of a given month. dow: 0=Sun … 6=Sat. n: 1-based.
// e.g. nthWeekdayOfMonth(2026, 1, 1, 3) → 3rd Monday of Feb 2026 (= Feb 16).
function nthWeekdayOfMonth(year, month, dow, n) {
  const first = dateAt(year, month, 1)
  const firstDow = first.getDay()
  const offset = (dow - firstDow + 7) % 7
  return dateAt(year, month, 1 + offset + (n - 1) * 7)
}

// Returns the Monday nearest to a given fixed date. If it's already a Monday, returns as-is.
// If between two Mondays, ties go to the preceding Monday.
function nearestMonday(fixedDate) {
  const dow = fixedDate.getDay()
  // Distance to nearest Monday (1). Backward distance: (dow - 1 + 7) % 7. Forward: (1 - dow + 7) % 7.
  const back = (dow - 1 + 7) % 7
  const forward = (1 - dow + 7) % 7
  return back <= forward ? addDays(fixedDate, -back) : addDays(fixedDate, forward)
}

// --- Easter computus ------------------------------------------------------

// Meeus/Jones/Butcher algorithm for the Gregorian Easter Sunday.
// Returns a Date at local midnight.
export function computeEasterSunday(year) {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1 // 0-indexed
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return dateAt(year, month, day)
}

// --- Per-holiday date rules ----------------------------------------------

// Each entry maps a holiday id → a function (year) → { start, end }.
// end === start for single-day holidays. For ranges (school breaks, Christmas
// pairs), end is the last date INCLUSIVE.
//
// School break ranges are per-jurisdiction defaults. Cases can override via
// the holiday's dateRange field (see resolveHolidayDate).
const HOLIDAY_DATE_RULES = {
  // --- Statutory & commonly-observed, single-day ---
  'family-day': (y) => single(nthWeekdayOfMonth(y, 1, 1, 3)),           // 3rd Monday of February
  'louis-riel-day': (y) => single(nthWeekdayOfMonth(y, 1, 1, 3)),        // 3rd Monday of February (MB)
  'islander-day': (y) => single(nthWeekdayOfMonth(y, 1, 1, 3)),          // 3rd Monday of February (PE)
  'heritage-day-ns': (y) => single(nthWeekdayOfMonth(y, 1, 1, 3)),       // 3rd Monday of February (NS)
  'st-patricks-day': (y) => single(nearestMonday(dateAt(y, 2, 17))),      // Nearest Monday to Mar 17 (NL)
  'good-friday': (y) => single(addDays(computeEasterSunday(y), -2)),
  'easter-monday': (y) => single(addDays(computeEasterSunday(y), 1)),
  'st-georges-day': (y) => single(nearestMonday(dateAt(y, 3, 23))),       // Nearest Monday to Apr 23 (NL)
  'victoria-day': (y) => single(mondayBefore(dateAt(y, 4, 25))),          // Monday preceding May 25
  'national-indigenous-peoples-day': (y) => single(dateAt(y, 5, 21)),      // Jun 21 (fixed)
  'discovery-day-nl': (y) => single(nearestMonday(dateAt(y, 5, 24))),      // Nearest Monday to Jun 24 (NL)
  'canada-day': (y) => single(dateAt(y, 6, 1)),                            // Jul 1
  'memorial-day': (y) => single(dateAt(y, 6, 1)),                          // Jul 1 (NL, observed alongside Canada Day)
  'nunavut-day': (y) => single(dateAt(y, 6, 9)),                           // Jul 9 (NU)
  'orangemans-day': (y) => single(nearestMonday(dateAt(y, 6, 12))),        // Nearest Monday to Jul 12 (NL)
  'bc-day': (y) => single(nthWeekdayOfMonth(y, 7, 1, 1)),                  // 1st Monday of August
  'heritage-day': (y) => single(nthWeekdayOfMonth(y, 7, 1, 1)),            // 1st Monday of August (AB)
  'terry-fox-day': (y) => single(nthWeekdayOfMonth(y, 7, 1, 1)),           // 1st Monday of August (MB)
  'new-brunswick-day': (y) => single(nthWeekdayOfMonth(y, 7, 1, 1)),       // 1st Monday of August (NB)
  'saskatchewan-day': (y) => single(nthWeekdayOfMonth(y, 7, 1, 1)),        // 1st Monday of August (SK)
  'civic-holiday': (y) => single(nthWeekdayOfMonth(y, 7, 1, 1)),           // 1st Monday of August (ON, NS, NT, NU)
  'gold-cup-parade-day': (y) => single(nthWeekdayOfMonth(y, 7, 5, 3)),     // 3rd Friday of August (PE)
  'discovery-day': (y) => single(nthWeekdayOfMonth(y, 7, 1, 3)),           // 3rd Monday of August (YT)
  'labour-day': (y) => single(nthWeekdayOfMonth(y, 8, 1, 1)),              // 1st Monday of September
  'truth-reconciliation': (y) => single(dateAt(y, 8, 30)),                 // Sep 30 (fixed)
  'thanksgiving': (y) => single(nthWeekdayOfMonth(y, 9, 1, 2)),            // 2nd Monday of October
  'remembrance-day': (y) => single(dateAt(y, 10, 11)),                     // Nov 11 (fixed)

  // --- Special days ---
  'mothers-day': (y) => single(nthWeekdayOfMonth(y, 4, 0, 2)),             // 2nd Sunday of May
  'fathers-day': (y) => single(nthWeekdayOfMonth(y, 5, 0, 3)),             // 3rd Sunday of June
  'halloween': (y) => single(dateAt(y, 9, 31)),                            // Oct 31
  // Birthdays + pro-d-days + children's-birthday have no canonical date — caller supplies via dateRange.

  // --- Christmas / New Year (Eve+Day pairs treated as ranges) ---
  'christmas-eve': (y) => range(dateAt(y, 11, 24), dateAt(y, 11, 24)),      // Dec 24 (single day; pairing is a rule concern)
  'christmas-day': (y) => range(dateAt(y, 11, 25), dateAt(y, 11, 25)),      // Dec 25
  'new-years-eve': (y) => range(dateAt(y, 11, 31), dateAt(y, 11, 31)),      // Dec 31
  'new-years-day': (y) => range(dateAt(y, 0, 1), dateAt(y, 0, 1)),          // Jan 1

  // --- School breaks (per-jurisdiction defaults, override via holiday.dateRange) ---
  'spring-break': (y, jur) => SCHOOL_BREAK_DEFAULTS[jur || 'BC']?.spring?.(y) ?? SCHOOL_BREAK_DEFAULTS.BC.spring(y),
  'summer-break': (y, jur) => SCHOOL_BREAK_DEFAULTS[jur || 'BC']?.summer?.(y) ?? SCHOOL_BREAK_DEFAULTS.BC.summer(y),
  'winter-break': (y, jur) => SCHOOL_BREAK_DEFAULTS[jur || 'BC']?.winter?.(y) ?? SCHOOL_BREAK_DEFAULTS.BC.winter(y),
}

// --- Helpers used by the rule table --------------------------------------

function single(d) { return { start: d, end: d } }
function range(start, end) { return { start, end } }

// Monday preceding a fixed date (used for Victoria Day: Monday before May 25).
function mondayBefore(fixedDate) {
  const dow = fixedDate.getDay()
  const back = (dow - 1 + 7) % 7 || 7 // if today is Monday, want the PREVIOUS Monday, so 7 back
  return addDays(fixedDate, -back)
}

// --- School break defaults (per province) --------------------------------
//
// These are approximations of school-district calendars. The `dateRange`
// override on a holiday should be used when a family's actual district
// diverges — e.g. British Columbia's spring break varies by district
// (some 2 weeks, some 1 week; sometimes mid-March, sometimes late March).

const SCHOOL_BREAK_DEFAULTS = {
  BC: {
    spring: (y) => range(nthWeekdayOfMonth(y, 2, 1, 3), addDays(nthWeekdayOfMonth(y, 2, 1, 3), 11)),  // 2 weeks starting 3rd Monday of March
    summer: (y) => range(dateAt(y, 5, 29), dateAt(y, 7, 31)),                                          // Late Jun – End Aug
    winter: (y) => range(dateAt(y, 11, 20), dateAt(y + 1, 0, 2)),                                      // Dec 20 – Jan 2
  },
  AB: {
    spring: (y) => range(nthWeekdayOfMonth(y, 2, 1, 4), addDays(nthWeekdayOfMonth(y, 2, 1, 4), 4)),
    summer: (y) => range(dateAt(y, 5, 27), dateAt(y, 7, 31)),
    winter: (y) => range(dateAt(y, 11, 20), dateAt(y + 1, 0, 2)),
  },
  MB: {
    spring: (y) => range(nthWeekdayOfMonth(y, 2, 1, 4), addDays(nthWeekdayOfMonth(y, 2, 1, 4), 4)),
    summer: (y) => range(dateAt(y, 5, 27), dateAt(y, 7, 31)),
    winter: (y) => range(dateAt(y, 11, 20), dateAt(y + 1, 0, 2)),
  },
  NB: {
    spring: (y) => range(nthWeekdayOfMonth(y, 2, 1, 1), addDays(nthWeekdayOfMonth(y, 2, 1, 1), 4)),
    summer: (y) => range(dateAt(y, 5, 27), dateAt(y, 7, 31)),
    winter: (y) => range(dateAt(y, 11, 20), dateAt(y + 1, 0, 2)),
  },
  NL: {
    spring: (y) => range(nthWeekdayOfMonth(y, 2, 1, 3), addDays(nthWeekdayOfMonth(y, 2, 1, 3), 4)),
    summer: (y) => range(dateAt(y, 5, 27), dateAt(y, 7, 31)),
    winter: (y) => range(dateAt(y, 11, 20), dateAt(y + 1, 0, 2)),
  },
  NS: {
    spring: (y) => range(nthWeekdayOfMonth(y, 2, 1, 3), addDays(nthWeekdayOfMonth(y, 2, 1, 3), 4)),
    summer: (y) => range(dateAt(y, 5, 27), dateAt(y, 7, 31)),
    winter: (y) => range(dateAt(y, 11, 20), dateAt(y + 1, 0, 2)),
  },
  NT: {
    spring: (y) => range(nthWeekdayOfMonth(y, 2, 1, 3), addDays(nthWeekdayOfMonth(y, 2, 1, 3), 4)),
    summer: (y) => range(dateAt(y, 5, 20), dateAt(y, 7, 25)),
    winter: (y) => range(dateAt(y, 11, 20), dateAt(y + 1, 0, 2)),
  },
  NU: {
    spring: (y) => range(nthWeekdayOfMonth(y, 2, 1, 3), addDays(nthWeekdayOfMonth(y, 2, 1, 3), 4)),
    summer: (y) => range(dateAt(y, 5, 20), dateAt(y, 7, 25)),
    winter: (y) => range(dateAt(y, 11, 20), dateAt(y + 1, 0, 2)),
  },
  ON: {
    spring: (y) => range(nthWeekdayOfMonth(y, 2, 1, 2), addDays(nthWeekdayOfMonth(y, 2, 1, 2), 4)),
    summer: (y) => range(dateAt(y, 5, 27), dateAt(y, 7, 31)),
    winter: (y) => range(dateAt(y, 11, 20), dateAt(y + 1, 0, 2)),
  },
  PE: {
    spring: (y) => range(nthWeekdayOfMonth(y, 2, 1, 3), addDays(nthWeekdayOfMonth(y, 2, 1, 3), 4)),
    summer: (y) => range(dateAt(y, 5, 27), dateAt(y, 7, 31)),
    winter: (y) => range(dateAt(y, 11, 20), dateAt(y + 1, 0, 2)),
  },
  SK: {
    spring: (y) => range(nthWeekdayOfMonth(y, 2, 1, 4), addDays(nthWeekdayOfMonth(y, 2, 1, 4), 4)),
    summer: (y) => range(dateAt(y, 5, 27), dateAt(y, 7, 31)),
    winter: (y) => range(dateAt(y, 11, 20), dateAt(y + 1, 0, 2)),
  },
  YT: {
    spring: (y) => range(nthWeekdayOfMonth(y, 2, 1, 3), addDays(nthWeekdayOfMonth(y, 2, 1, 3), 4)),
    summer: (y) => range(dateAt(y, 5, 20), dateAt(y, 7, 25)),
    winter: (y) => range(dateAt(y, 11, 20), dateAt(y + 1, 0, 2)),
  },
}

// --- Public API ----------------------------------------------------------

// Resolve a single holiday to its concrete date range for a given year.
//
//   resolveHolidayDate('family-day', 2026)
//     → { start: 2026-02-16 (Mon), end: 2026-02-16 (Mon) }
//
//   resolveHolidayDate('spring-break', 2026, { jurisdiction: 'BC' })
//     → { start: 2026-03-16 (Mon), end: 2026-03-27 (Fri) }
//
//   resolveHolidayDate('custom-anything', 2026, { dateRange: { start: '2026-05-01', end: '2026-05-03' } })
//     → uses the supplied override
//
// Returns null when the holiday has no canonical date rule AND no override
// (e.g. birthdays, professional development days, custom-added rows).
export function resolveHolidayDate(holidayId, year, opts = {}) {
  // Year-specific dates win (school breaks change with every school
  // calendar): opts.dateRanges = { 2026: { start: '2026-03-09', end:
  // '2026-03-20' }, 2027: {...} }. Dates are literal, keyed by the year the
  // range STARTS in (a winter break entry under 2026 may end in Jan 2027).
  // Years without an entry fall through to the recurring dateRange, then to
  // the built-in rule / provincial default.
  const perYear = opts.dateRanges?.[year]
  if (perYear && perYear.start && perYear.end) {
    return { start: coerceDate(perYear.start), end: coerceDate(perYear.end) }
  }
  if (opts.dateRange && opts.dateRange.start && opts.dateRange.end) {
    // User-set dates RECUR ANNUALLY: only the month/day of start and end are
    // kept and re-projected into the requested year, so a birthday or school
    // break set once paints on every year of the calendar. Accepts 'MM-DD',
    // 'YYYY-MM-DD' (year ignored), or Date. An end month/day earlier than the
    // start wraps into the following year (winter break Dec 20 - Jan 2).
    const s = coerceMonthDay(opts.dateRange.start)
    const e = coerceMonthDay(opts.dateRange.end)
    if (s && e) {
      const wraps = e.month < s.month || (e.month === s.month && e.day < s.day)
      return {
        start: dateAt(year, s.month, s.day),
        end: dateAt(wraps ? year + 1 : year, e.month, e.day),
      }
    }
  }
  const rule = HOLIDAY_DATE_RULES[holidayId]
  if (!rule) return null
  return rule(year, opts.jurisdiction)
}

// True when schedule-core can compute this holiday's date by itself (stat
// holidays, Easter chain, school-break defaults). False for birthdays,
// pro-d days and user-added holidays — those need a user-set dateRange
// before they can appear on calendars.
export function hasDateRule(holidayId) {
  return !!HOLIDAY_DATE_RULES[holidayId]
}

// '03-16', '2026-03-16', or Date → { month (0-based), day } | null
function coerceMonthDay(x) {
  if (x instanceof Date) return { month: x.getMonth(), day: x.getDate() }
  const m = /^(?:(\d{4})-)?(\d{1,2})-(\d{1,2})$/.exec(String(x || '').trim())
  if (!m) return null
  return { month: Number(m[2]) - 1, day: Number(m[3]) }
}

// Resolve every seeded holiday for a jurisdiction in a given year. Filters
// out entries whose date rule doesn't exist (e.g. birthdays).
export function resolveHolidaysForYear(year, jurisdiction) {
  const out = []
  for (const seed of SEED_HOLIDAYS) {
    const dr = resolveHolidayDate(seed.id, year, { jurisdiction })
    if (dr) out.push({ id: seed.id, name: seed.name, category: seed.category, start: dr.start, end: dr.end })
  }
  return out
}

// Given a date and a list of holidays (with their configured rules + optional
// dateRange overrides), return the holiday whose date range covers the date,
// along with resolved start/end. Returns null if the date isn't covered.
//
// When several holidays overlap, the NARROWEST range wins: a specific day
// rule (Christmas Day) out-ranks the broad range that contains it (Winter
// Break). Ties keep list order.
export function holidayCoveringDate(date, holidays, opts = {}) {
  return holidaysCoveringDate(date, holidays, opts)[0] || null
}

// ALL holidays covering the date, sorted narrowest range first. Callers that
// resolve outcomes should walk the list and take the first holiday whose
// rule yields a concrete answer — so an undecided Christmas Day row defers
// to the Winter Break rule that surrounds it, rather than jumping straight
// back to the weekly rotation.
export function holidaysCoveringDate(date, holidays, opts = {}) {
  const year = date.getFullYear()
  const jurisdiction = opts.jurisdiction
  const target = stripTime(date).getTime()
  const hits = []
  for (const h of holidays) {
    // Ranges that cross New Year (winter break Dec 20 - Jan 2) cover January
    // dates via the PREVIOUS year's resolution, so test both years.
    for (const y of [year, year - 1]) {
      const dr = resolveHolidayDate(h.id, y, {
        jurisdiction,
        dateRange: h.dateRange,
        dateRanges: h.dateRanges,
      })
      if (!dr) continue
      const s = stripTime(dr.start).getTime()
      const e = stripTime(dr.end).getTime()
      if (target >= s && target <= e) {
        hits.push({ holiday: h, start: dr.start, end: dr.end, span: e - s })
        break // one covering resolution per holiday is enough
      }
    }
  }
  hits.sort((a, b) => a.span - b.span)
  return hits
}

// --- Small internal utilities --------------------------------------------

function stripTime(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function coerceDate(x) {
  if (x instanceof Date) return x
  // Accept 'YYYY-MM-DD' as a local-midnight Date (not UTC — matters for calendar rendering).
  if (typeof x === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(x)
    if (m) return dateAt(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    return new Date(x)
  }
  return new Date(x)
}
